import React, { useState } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import { fmtRM, fmtRM0, SPECIAL_CATEGORIES } from "../../lib/taxConfig";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const GRID = "grid grid-cols-[30px_minmax(0,1.4fr)_minmax(0,1fr)_112px_112px_78px] gap-2 items-center";

const sumDisallowed = (notes) =>
  (notes || []).reduce((s, x) => s + (x.treatment === "disallowed" ? Number(x.amount || 0) : 0), 0);

const DisallowableRows = ({ rows, onChange }) => {
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState({ treatment: "disallowed", ab: "", disallowed: 0, payee: "", notes: [], specialCategory: "" });

  const update = (idx, field, value) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const remove = (idx) =>
    onChange(rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, noteNo: i + 1 })));
  const add = () =>
    onChange([...rows, { noteNo: rows.length + 1, description: "", remark: "", amount: 0, disallowed: 0, treatment: "allowed", notes: [] }]);

  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalDisallowed = rows.reduce((s, r) => s + Number(r.disallowed || 0), 0);

  const hasNotes = (r) => (r.notes && r.notes.length > 0) || !!r.note;
  let n = 0;
  const noteRef = rows.map((r) => (hasNotes(r) ? ++n : null));

  const openAdjust = (idx) => {
    const r = rows[idx];
    const treatment = r.treatment || (Number(r.disallowed) > 0 ? "disallowed" : "allowed");
    const notes = (r.notes && r.notes.length)
      ? r.notes.map((x) => ({ amount: x.amount ?? "", text: x.text || "", treatment: x.treatment || "disallowed", specialCategory: x.specialCategory || "" }))
      : (r.note ? [{ amount: "", text: r.note, treatment: treatment === "allowed" ? "allowed" : "disallowed" }] : []);
    setDraft({ treatment, ab: r.ab || "", disallowed: r.disallowed || 0, payee: r.payee || "", notes, specialCategory: r.specialCategory || "" });
    setEditIdx(idx);
  };

  const hasSubnotes = draft.notes.length > 0;
  const isAllowed = draft.treatment === "allowed";

  const setTreatment = (t) =>
    setDraft((d) => (t === "allowed" ? { ...d, treatment: "allowed", ab: "", disallowed: 0 } : { ...d, treatment: "disallowed" }));

  const setAb = (ab) => {
    const amt = Number(rows[editIdx]?.amount || 0);
    const dis = ab === "" ? draft.disallowed : Math.round(amt * (Number(ab) / 100));
    setDraft((d) => ({ ...d, ab, disallowed: dis }));
  };

  const addNote = () => setDraft((d) => ({ ...d, notes: [...d.notes, { amount: "", text: "", treatment: "allowed", specialCategory: "" }] }));
  const updateNote = (i, field, val) =>
    setDraft((d) => ({ ...d, notes: d.notes.map((x, j) => (j === i ? { ...x, [field]: val } : x)) }));
  const removeNote = (i) => setDraft((d) => ({ ...d, notes: d.notes.filter((_, j) => j !== i) }));

  const draftDisallowed = hasSubnotes ? sumDisallowed(draft.notes) : Number(draft.disallowed || 0);

  const saveAdjust = () => {
    const notes = draft.notes
      .map((x) => ({ amount: x.amount === "" ? "" : Number(x.amount || 0), text: (x.text || "").trim(), treatment: x.treatment || "disallowed", specialCategory: x.treatment === "disallowed" ? (x.specialCategory || "") : "" }))
      .filter((x) => x.text || x.amount !== "");
    let disallowed, treatment;
    if (notes.length) {
      disallowed = sumDisallowed(notes);
      treatment = disallowed > 0 ? "disallowed" : "allowed";
    } else {
      const allowed = draft.treatment === "allowed";
      disallowed = allowed ? 0 : Number(draft.disallowed || 0);
      treatment = draft.treatment;
    }
    onChange(rows.map((r, i) => (i === editIdx
      ? {
          ...r,
          treatment,
          ab: treatment === "allowed" ? "" : draft.ab,
          disallowed,
          payee: draft.payee,
          specialCategory: treatment === "allowed" ? "" : draft.specialCategory,
          notes,
          note: notes.map((x) => x.text).filter(Boolean).join("; "),
        }
      : r)));
    setEditIdx(null);
  };

  return (
    <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid="disallowable-rows">
      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-[#eef4fb] text-[#1877c9]">
        Add: Disallowable expenses under Sec.39(1)
      </div>
      <div className={`${GRID} px-3 py-1.5 text-[11px] text-gray-400 border-b border-gray-100`}>
        <span>No.</span><span>Description</span><span>Remark</span>
        <span className="text-right">Amount</span><span className="text-right">Disallowed</span><span></span>
      </div>

      {rows.map((r, idx) => (
        <div key={idx} className={`${GRID} px-3 py-1.5 border-b border-gray-100`}>
          <span className="text-xs text-gray-400">{idx + 1}</span>
          <input value={r.description} onChange={(e) => update(idx, "description", e.target.value)} placeholder="Expense"
            className="text-sm px-2 py-1 border border-transparent hover:border-gray-200 focus:border-[#1877c9] rounded outline-none w-full" />
          <input value={r.remark} onChange={(e) => update(idx, "remark", e.target.value)} placeholder="Remark"
            className="text-sm px-2 py-1 border border-transparent hover:border-gray-200 focus:border-[#1877c9] rounded outline-none w-full" />
          <input type="number" value={r.amount} onChange={(e) => update(idx, "amount", e.target.value)}
            className="text-right text-sm font-mono px-2 py-1 border border-gray-200 focus:border-[#1877c9] rounded outline-none w-full" />
          <input type="number" value={r.disallowed} onChange={(e) => update(idx, "disallowed", e.target.value)}
            className="text-right text-sm font-mono px-2 py-1 border border-gray-200 focus:border-[#1877c9] rounded outline-none w-full" />
          <div className="flex items-center justify-end gap-0.5">
            {noteRef[idx] && (
              <span className="text-[10px] font-semibold text-[#1877c9] bg-[#eef4fb] rounded px-1 leading-4" title={`Note ${noteRef[idx]}`}>N{noteRef[idx]}</span>
            )}
            <button data-testid={`adjust-row-${idx}`} onClick={() => openAdjust(idx)} title="Adjust & notes" className="p-1 text-gray-400 hover:text-[#1877c9] hover:bg-blue-50 rounded"><Info size={14} /></button>
            <button data-testid={`remove-row-${idx}`} onClick={() => remove(idx)} title="Remove" className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}

      {/* Totals */}
      <div className={`${GRID} px-3 py-2 bg-[#f7f9fc] border-b border-gray-100`}>
        <span></span>
        <span className="text-sm font-semibold text-gray-700">Total</span>
        <span></span>
        <span className="text-right text-sm font-semibold font-mono text-gray-800" data-testid="disallowable-total-amount">{fmtRM0(totalAmount)}</span>
        <span className="text-right text-sm font-semibold font-mono text-gray-800" data-testid="disallowable-total-disallowed">{fmtRM0(totalDisallowed)}</span>
        <span></span>
      </div>

      <div className="px-3 py-2 bg-[#f7f9fc]">
        <button data-testid="add-disallowable-btn" onClick={add} className="flex items-center gap-1 text-sm text-[#1877c9] hover:underline"><Plus size={15} /> Add expense</button>
      </div>

      {/* Adjustment dialog */}
      <Dialog open={editIdx !== null} onOpenChange={(o) => !o && setEditIdx(null)}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Disallowed Amount</DialogTitle>
            <DialogDescription>{rows[editIdx]?.description || "Expense"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex items-center justify-between bg-[#f7f9fc] rounded px-3 py-2">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="text-sm font-mono font-semibold">{fmtRM(rows[editIdx]?.amount)}</span>
            </div>

            {!hasSubnotes ? (
              <>
                {/* Simple mode — whole expense */}
                <div className="space-y-1.5">
                  <Label>Treatment</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="treatment-allowed" onClick={() => setTreatment("allowed")}
                      className={`text-sm rounded border px-3 py-2 transition-colors ${draft.treatment === "allowed" ? "border-[#1877c9] bg-[#eef4fb] text-[#1877c9] font-semibold" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>Allowed</button>
                    <button type="button" data-testid="treatment-disallowed" onClick={() => setTreatment("disallowed")}
                      className={`text-sm rounded border px-3 py-2 transition-colors ${draft.treatment === "disallowed" ? "border-[#1877c9] bg-[#eef4fb] text-[#1877c9] font-semibold" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>Disallowed</button>
                  </div>
                  <p className="text-xs text-gray-400">{isAllowed ? "Expense stays fully deductible." : "Add back all or part of this expense. Or split it into subnotes below."}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Add-back % (AB%)</Label>
                    <Input type="number" value={draft.ab} onChange={(e) => setAb(e.target.value)} placeholder="e.g. 50" disabled={isAllowed} data-testid="adjust-ab" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Disallowed amount</Label>
                    <Input type="number" value={draft.disallowed} onChange={(e) => setDraft({ ...draft, disallowed: e.target.value, ab: "" })} className="font-mono text-right" disabled={isAllowed} data-testid="adjust-disallowed" />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between bg-[#eef4fb] rounded px-3 py-2">
                <span className="text-sm text-[#1877c9] font-medium">Disallowed (from subnotes)</span>
                <span className="text-sm font-mono font-semibold text-[#1877c9]" data-testid="computed-disallowed">{fmtRM(draftDisallowed)}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Payee (optional)</Label>
              <Input value={draft.payee} onChange={(e) => setDraft({ ...draft, payee: e.target.value })} placeholder="Paid to..." data-testid="adjust-payee" />
            </div>

            {/* Subnotes — split the expense; each is Disallowed or Allowed */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Subnotes (breakdown)</Label>
                <button type="button" data-testid="add-explanation" onClick={addNote} className="flex items-center gap-1 text-xs text-[#1877c9] hover:underline"><Plus size={13} /> Add subnote</button>
              </div>
              {draft.notes.map((x, i) => (
                <div key={i} className="border border-gray-200 rounded p-2 space-y-2" data-testid={`explanation-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      <button type="button" data-testid={`subnote-allowed-${i}`} onClick={() => updateNote(i, "treatment", "allowed")}
                        className={`text-xs rounded border px-2 py-1 transition-colors ${x.treatment === "allowed" ? "border-[#1877c9] bg-[#eef4fb] text-[#1877c9] font-semibold" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>Allowed</button>
                      <button type="button" data-testid={`subnote-disallowed-${i}`} onClick={() => updateNote(i, "treatment", "disallowed")}
                        className={`text-xs rounded border px-2 py-1 transition-colors ${x.treatment === "disallowed" ? "border-[#1877c9] bg-[#eef4fb] text-[#1877c9] font-semibold" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>Disallowed</button>
                    </div>
                    <button type="button" onClick={() => removeNote(i)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`remove-explanation-${i}`} title="Remove subnote"><Trash2 size={14} /></button>
                  </div>
                  <div className="flex items-start gap-2">
                    <Input type="number" value={x.amount} onChange={(e) => updateNote(i, "amount", e.target.value)} placeholder="Amount" className="w-24 font-mono text-right h-9 shrink-0" data-testid={`explanation-amount-${i}`} />
                    <Textarea value={x.text} onChange={(e) => updateNote(i, "text", e.target.value)} placeholder="Explanation" rows={2} className="flex-1 min-h-[36px]" data-testid={`explanation-text-${i}`} />
                  </div>
                  {x.treatment === "disallowed" && (
                    <div className="space-y-1">
                      <select value={x.specialCategory || ""} onChange={(e) => updateNote(i, "specialCategory", e.target.value)}
                        className="w-full h-8 border border-gray-200 rounded px-2 text-xs bg-white focus:border-[#1877c9] outline-none" data-testid={`subnote-category-${i}`}>
                        <option value="">Claim as special deduction — None —</option>
                        {SPECIAL_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-400">A numbered note listing these subnotes appears in the tax computation report.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIdx(null)}>Cancel</Button>
            <Button onClick={saveAdjust} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="adjust-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisallowableRows;
